import { CANVAS_LINK_TEXT_ID } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';
import { DocumentElement, TextElement } from '@tencent/alloy-editor';
import { RectText, RectTextOption } from '@tencent/docs-editor';

// 管理数据节点 textElement 和实际渲染节点 textNode 之间的映射
class TextElementToTextNode {
    private map: WeakMap<TextElement, Set<LinkText>> = new WeakMap();
    public link(textElement: TextElement, textNode: LinkText): void {
        if (!this.map.has(textElement)) {
            this.map.set(textElement, new Set());
        }
        this.map.get(textElement)?.add(textNode);
    }

    public get(textElement: TextElement): Array<LinkText> {
        return Array.from(this.map.get(textElement) || []);
    }

    public unlink(textElement: TextElement, textNode: LinkText): void {
        if (this.map.has(textElement)) {
            const set = this.map.get(textElement);
            set?.delete(textNode);
            if (!set?.size) {
                this.map.delete(textElement);
            }
        }
    }

    /**
     * 手动迁移映射关系
     * 常规更新映射的逻辑是在链接文字绘制时触发，但是如果 editor 数据变更没有重新渲染的话，就需要手动调用这个方法迁移映射关系
     * 这个逻辑可行的前提是变更前后 editor 的有效数据是一一对应的，
     * @param oldDocumentElement
     * @param newDocumentElement
     */
    public migrate(oldDocumentElement: DocumentElement, newDocumentElement: DocumentElement) {
        const { children: oldParagraphs = [] } = oldDocumentElement;
        const { children: newParagraphs = [] } = newDocumentElement;
        oldParagraphs.forEach((oldParagraph, paragraphIndex) => {
            const newParagraph = newParagraphs[paragraphIndex];
            if (!oldParagraph || !newParagraph) {
                return;
            }
            const { children: oldTextElements = [] } = oldParagraph;
            const { children: newTextElements = [] } = newParagraph;
            oldTextElements.forEach((oldTextElement, textElementIndex) => {
                const newTextElement = newTextElements[textElementIndex];
                if (this.map.has(oldTextElement)) {
                    const textNodes = this.map.get(oldTextElement) || new Set();
                    // 更新每个文字节点绑定的 textElement
                    textNodes.forEach((textNode) => {
                        textNode.updateTextElement(newTextElement);
                    });
                    this.map.set(newTextElement, textNodes);
                    this.map.delete(oldTextElement);
                }
            });
        });
    }
}

export const textElementToTextNode = new TextElementToTextNode();

/**
 * 链接文字节点，每个链接文字节点都和对应的 textElement 绑定
 */
export class LinkText extends RectText {
    public textElement: TextElement;

    public constructor(text: string, option: RectTextOption, textElement: TextElement) {
        super(text, option);
        this.textElement = textElement;
        textElementToTextNode.link(textElement, this);
        this.id = CANVAS_LINK_TEXT_ID;
    }

    /**
     * 更新当前文字节点对应的 textElement，在 textElement 改变但是没重新绘制时需要调用
     * @param textElement
     */
    public updateTextElement(textElement: TextElement): void {
        this.textElement = textElement;
    }

    public dispose(): void {
        super.dispose();
        // 销毁时解除和 textElement 的映射关系
        textElementToTextNode.unlink(this.textElement, this);
    }
}
